// /help: the self-service hub. Track an order, fix a problem, search the answers, or chat.
// Chat buttons ([data-chat]) open Fin in the Intercom Messenger with an unsent starter. They are plain email links in
// the HTML, so if the Messenger is blocked, offline or refused, the click still opens an email.
import { $, $$, initChrome, reducedMotion } from 'v2/ui.js';
import { optedOut } from 'v2/privacy.js';

// Where "Track my order" goes: the AfterShip branded tracking page (order number and email, or a tracking number),
// live on track.drinkproda.com since 2026-10-06. Shopify's order list (account.drinkproda.com/orders) is the fallback.
export const TRACK_URL = 'https://track.drinkproda.com';
const INTERCOM_APP_ID = 'axzmv915';
const INTERCOM_API = 'https://api-iam.intercom.io';
const READY_WAIT_MS = 5000;

// Unsent first lines for each entry point. ?chat=<key> in the URL opens the same starter (emails link here).
// Never send on the customer's behalf; they can edit or delete the line.
const STARTERS = {
  chat: '',
  'order-help': 'I need help with my order.',
  'order-problem': 'Something’s wrong with my order.',
  'order-change': 'I need to change or cancel my order.',
  creator: 'I’m a creator or athlete and I’d like to work with PRODA.',
  partnership: 'I have an event or partnership idea for PRODA.',
  press: 'I’m with the press.',
  business: 'I’d like to talk about investing in PRODA or a business proposal.',
  product: 'I have a question about my can.',
  wholesale: 'I’d like to stock PRODA in my store.',
  careers: 'I’d like to ask about working at PRODA.',
};
// The live theme's links (/pages/contact?intent=<name>&chat=1, from emails and old pages) land here after the move.
const INTENTS = { support: 'chat', general: 'chat', order: 'order-help', product: 'product', creator: 'creator', partnership: 'partnership',
  event: 'partnership', business: 'business', investor: 'business', wholesale: 'wholesale', careers: 'careers', press: 'press' };

initChrome();
$$('[data-track-link]').forEach((a) => { a.href = TRACK_URL; });

/* ---------- Messenger ---------- */
let messenger = null; // a promise: resolves true once booted, false if it failed or timed out

function booted() {
  return typeof window.Intercom === 'function' && window.Intercom.booted === true;
}

function loadMessenger() {
  if (messenger) return messenger;
  messenger = new Promise((resolve) => {
    window.intercomSettings = { api_base: INTERCOM_API, app_id: INTERCOM_APP_ID };
    if (typeof window.Intercom !== 'function') {
      const queue = function queued() { queue.c(arguments); };
      queue.q = [];
      queue.c = (args) => queue.q.push(args);
      window.Intercom = queue;
    }
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
    script.onerror = () => resolve(false);
    document.head.append(script);
    const started = performance.now();
    (function poll() {
      if (booted()) return resolve(true);
      if (performance.now() - started > 15000) return resolve(false);
      setTimeout(poll, 150);
    })();
  });
  return messenger;
}

// fallback: follow the link's email address when the Messenger can't open. Only for a click, never on page load.
async function openChat(link, text, { fallback = true } = {}) {
  const ready = booted() || await Promise.race([loadMessenger(), new Promise((r) => setTimeout(() => r(false), READY_WAIT_MS))]);
  if (!ready || !booted()) {
    if (fallback && link?.href) location.href = link.href;
    return;
  }
  if (text) window.Intercom('showNewMessage', text);
  else window.Intercom('showNewMessage');
}

document.addEventListener('click', (e) => {
  const link = e.target.closest('[data-chat]');
  if (!link) return;
  e.preventDefault();
  const query = link.hasAttribute('data-chat-query') ? $('[data-help-q]')?.value.trim() : '';
  const text = query ? `My question: ${query}` : (STARTERS[link.dataset.chat] ?? '');
  link.setAttribute('aria-busy', 'true');
  openChat(link, text).finally(() => link.removeAttribute('aria-busy'));
});

// Load the Messenger once the page is idle, so the first chat click opens at once. With a privacy opt-out (or Global
// Privacy Control) it loads only when someone asks to chat.
if (!optedOut()) {
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
  addEventListener('load', () => idle(() => loadMessenger()), { once: true });
}

/* ---------- Answers: search and deep links ---------- */
const fold = (s) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[’‘]/g, "'");
const answers = $$('.help__groups .faq__item').map((el) => ({
  el,
  id: el.id,
  q: $('summary', el).textContent.trim(),
  question: fold($('summary', el).textContent),
  text: fold(el.textContent),
}));

function openAnswer(id, { focus = true } = {}) {
  const item = document.getElementById(id);
  if (!item || !item.matches('.faq__item')) return;
  item.open = true;
  item.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
  if (focus) $('summary', item).focus({ preventScroll: true });
  history.replaceState(null, '', `#${id}`);
}

const form = $('[data-help-search]');
const input = $('[data-help-q]');
const box = $('[data-help-results]');
const list = $('[data-help-list]');
const count = $('[data-help-count]');
const none = $('[data-help-none]');

function search(query) {
  const words = fold(query).split(/[^a-z0-9']+/).filter((w) => w.length > 1);
  if (!words.length) return null;
  return answers
    .map((a) => ({ a, hit: words.every((w) => a.text.includes(w)), score: words.filter((w) => a.question.includes(w)).length }))
    .filter((r) => r.hit)
    .sort((x, y) => y.score - x.score)
    .map((r) => r.a);
}

function show(query) {
  const found = search(query);
  if (!found) { box.hidden = true; return []; }
  box.hidden = false;
  const top = found.slice(0, 6);
  list.replaceChildren(...top.map((a) => {
    const li = document.createElement('li');
    const link = document.createElement('a');
    link.href = `#${a.id}`;
    link.textContent = a.q;
    link.addEventListener('click', (e) => { e.preventDefault(); openAnswer(a.id); });
    li.append(link);
    return li;
  }));
  count.textContent = found.length ? `${found.length} ${found.length === 1 ? 'answer' : 'answers'}` : '';
  none.hidden = found.length > 0;
  return found;
}

if (form && input) {
  let timer;
  input.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(() => show(input.value), 120); });
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const found = show(input.value);
    if (found.length) openAnswer(found[0].id);
    else if (input.value.trim()) $('a', none)?.focus();
  });
}

// /help#q-milk opens that answer; /help?q=shipping searches; /help?chat=order-problem opens that chat starter.
const params = new URLSearchParams(location.search);
if (location.hash.startsWith('#q-')) openAnswer(location.hash.slice(1), { focus: false });
if (params.get('q') && input) { input.value = params.get('q').slice(0, 80); show(input.value); }
const topic = params.get('chat') === '1' ? INTENTS[params.get('intent')] || 'chat' : params.get('chat');
if (topic && Object.hasOwn(STARTERS, topic)) {
  openChat(null, STARTERS[topic], { fallback: false }).then(() => { if (!booted()) $('#order-help')?.scrollIntoView(); });
}
